import { apiRequest, buildQuery } from './client';

const normalizeReportStatus = (status) => {
  if (['INVESTIGATING', 'AWAITING_RESPONSE', 'ESCALATED'].includes(status)) return 'PROCESSING';
  if (['RESOLVED', 'CLOSED'].includes(status)) return 'ACTION_APPLIED';
  return status;
};

const normalizeReport = (report) => report ? { ...report, status: normalizeReportStatus(report.status) } : report;
const normalizeReportPage = (page) => ({ ...page, items: (page?.items || []).map(normalizeReport) });
const wireReportStatus = (status) => status === 'PROCESSING' ? 'INVESTIGATING' : status;

export const moderationApi = {
  readReplies: (replyIds) => apiRequest('/api/v1/me/report-replies/read', { method: 'POST', body: { replyIds } }),
  report: async (body) => normalizeReport(await apiRequest('/api/v1/reports', { method: 'POST', body })),
  myReports: async (options) => normalizeReportPage(await apiRequest(`/api/v1/me/reports${buildQuery(options)}`)),
  reportById: async (id) => normalizeReport(await apiRequest(`/api/v1/reports/${id}`)),
  queue: async (options) => normalizeReportPage(await apiRequest(`/api/v1/moderation/reports${buildQuery(options)}`)),
  inbox: async (options) => normalizeReportPage(await apiRequest(`/api/v1/moderation/reports/inbox${buildQuery(options)}`, { cache: false })),
  updateReport: async (id, body) => normalizeReport(await apiRequest(`/api/v1/moderation/reports/${id}`, { method: 'PATCH', body: { ...body, status: wireReportStatus(body.status) } })),
  addNote: async (id, body) => normalizeReport(await apiRequest(`/api/v1/moderation/reports/${id}/notes`, { method: 'POST', body })),
  linkReports: (id, body) => apiRequest(`/api/v1/moderation/reports/${id}/links`, { method: 'PUT', body }),
  act: (id, body) => apiRequest(`/api/v1/moderation/reports/${id}/actions`, { method: 'POST', body }),
  restrictions: (userId) => apiRequest(`/api/v1/moderation/users/${userId}/restrictions`),
  revokeRestriction: (id) => apiRequest(`/api/v1/admin/restrictions/${id}/revoke`, { method: 'POST' }),
  auditLog: (options) => apiRequest(`/api/v1/admin/moderation/audit-log${buildQuery(options)}`),
  actionRestrictions: (userId) => apiRequest(`/api/v1/admin/users/${userId}/action-restrictions`),
  restrictAction: (userId, body) => apiRequest(`/api/v1/admin/users/${userId}/action-restrictions`, { method: 'POST', body }),
  unrestrictAction: (userId, scope) => apiRequest(`/api/v1/admin/users/${userId}/action-restrictions/${encodeURIComponent(scope)}`, { method: 'DELETE' }),
};

export const accountDeletionApi = {
  pending: () => apiRequest('/api/v1/admin/account-deletions'),
  recover: (userId) => apiRequest(`/api/v1/admin/account-deletions/${userId}/recover`, { method: 'POST' }),
};

export const accessApi = {
  myPermissions: () => apiRequest('/api/v1/me/access/permissions'),
  permissions: () => apiRequest('/api/v1/admin/access/permissions'),
  roles: () => apiRequest('/api/v1/admin/access/roles'),
  createRole: (body) => apiRequest('/api/v1/admin/access/roles', { method: 'POST', body }),
  updateRole: (id, body) => apiRequest(`/api/v1/admin/access/roles/${id}`, { method: 'PUT', body }),
  setRolePermissions: (id, body) => apiRequest(`/api/v1/admin/access/roles/${id}/permissions`, { method: 'PUT', body }),
  removeRole: (id) => apiRequest(`/api/v1/admin/access/roles/${id}`, { method: 'DELETE' }),
  userAccess: (id) => apiRequest(`/api/v1/admin/access/users/${id}`),
  updateUserAccess: (id, body) => apiRequest(`/api/v1/admin/access/users/${id}`, { method: 'PUT', body }),
  userAvailability: (id) => apiRequest(`/api/v1/admin/access/users/${id}/availability`),
  setUserAvailability: (userId, roleId, body) => apiRequest(`/api/v1/admin/access/users/${userId}/roles/${roleId}/availability`, { method: 'PUT', body }),
  myAvailability: () => apiRequest('/api/v1/me/access/availability'),
  setMyAvailability: (roleId, body) => apiRequest(`/api/v1/me/access/roles/${roleId}/availability`, { method: 'PUT', body }),
  requestAvailability: (body) => apiRequest('/api/v1/me/access/availability-requests', { method: 'POST', body }),
  availabilityRequests: () => apiRequest('/api/v1/admin/access/availability-requests'),
  reviewAvailability: (id, body) => apiRequest(`/api/v1/admin/access/availability-requests/${id}`, { method: 'PUT', body }),
};
